import type { ColumnDef, ColumnSizingState } from '@tanstack/react-table'
import type { Ticket, TicketStatus, TicketPriority } from '@/types'
import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { SLAIndicator } from '@/components/ui/sla-indicator'
import {
  ticketStatusVariants as statusVariants,
  ticketPriorityVariants as priorityVariants,
} from '@/lib/ticket-variants'

interface TicketsColumnsProps {
  selectedIds?: Set<string>
  onToggleSelect?: (id: string) => void
  onToggleSelectAll?: () => void
  allSelected?: boolean
  formatDate?: (date?: string) => string
  /** When false, omits the selection checkbox column (read-only roles). Defaults to true. */
  enableSelection?: boolean
}

export const createTicketsColumns = ({
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  allSelected,
  formatDate,
  enableSelection = true,
}: TicketsColumnsProps = {}): ColumnDef<Ticket>[] => [
  ...(enableSelection
    ? [
        {
          id: 'select',
          header: () => (
            <Checkbox
              checked={allSelected}
              onCheckedChange={onToggleSelectAll}
              aria-label="Select all tickets"
            />
          ),
          cell: ({ row }) => (
            <div onClick={(e) => e.stopPropagation()}>
              <Checkbox
                checked={selectedIds?.has(row.original.id) ?? false}
                onCheckedChange={() => onToggleSelect?.(row.original.id)}
                aria-label={`Select ticket ${row.original.ticket_number}`}
              />
            </div>
          ),
          size: 40,
          minSize: 40,
          maxSize: 40,
          enableResizing: false,
        } as ColumnDef<Ticket>,
      ]
    : []),
  {
    id: 'ticket_number',
    header: '#',
    accessorKey: 'ticket_number',
    meta: { sortField: 'ticket_number' },
    size: 70,
    cell: ({ row }) => (
      <span className="font-mono text-sm text-muted-foreground">
        GRK-{row.original.ticket_number}
      </span>
    ),
  },
  {
    id: 'subject',
    header: 'Subject',
    accessorKey: 'subject',
    meta: { sortField: 'subject' },
    size: 280,
    cell: ({ row }) => (
      <Link
        to={`/tickets/${row.original.id}`}
        className="font-medium text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 truncate block max-w-[280px]"
      >
        {row.original.subject}
      </Link>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'status',
    meta: { sortField: 'status' },
    size: 100,
    cell: ({ row }) => (
      <Badge variant={statusVariants[row.original.status as TicketStatus] || 'default'}>
        {row.original.status}
      </Badge>
    ),
  },
  {
    id: 'priority',
    header: 'Priority',
    accessorKey: 'priority',
    meta: { sortField: 'priority' },
    size: 100,
    cell: ({ row }) => (
      <Badge variant={priorityVariants[row.original.priority as TicketPriority] || 'default'}>
        {row.original.priority}
      </Badge>
    ),
  },
  {
    id: 'sla',
    header: 'SLA',
    size: 100,
    cell: ({ row }) =>
      row.original.sla_status ? (
        <SLAIndicator
          status={row.original.sla_status.response_status}
          timeRemaining={row.original.sla_status.response_time_remaining_hours}
          size="sm"
        />
      ) : (
        <span className="text-muted-foreground">-</span>
      ),
  },
  {
    id: 'account_name',
    header: 'Account',
    accessorKey: 'account_name',
    meta: { sortField: 'account_name' },
    size: 160,
    cell: ({ row }) => (
      <span className="truncate block max-w-[160px]">{row.original.account_name || '-'}</span>
    ),
  },
  {
    id: 'contact_name',
    header: 'Contact',
    accessorKey: 'contact_name',
    meta: { sortField: 'contact_name' },
    size: 150,
    cell: ({ row }) => (
      <span className="truncate block max-w-[150px]">{row.original.contact_name || '-'}</span>
    ),
  },
  {
    id: 'product_name',
    header: 'Product',
    accessorKey: 'product_name',
    meta: { sortField: 'product_name' },
    size: 150,
    cell: ({ row }) => (
      <span className="truncate block max-w-[150px]">
        {row.original.product_name || (row.original as any).category || '-'}
      </span>
    ),
  },
  {
    id: 'jira',
    header: 'Jira',
    accessorKey: 'jira',
    meta: { sortField: 'jira' },
    size: 120,
    cell: ({ row }) => (
      <span className="text-sm truncate block max-w-[120px]">{row.original.jira || '-'}</span>
    ),
  },
  {
    id: 'owner_name',
    header: 'Owner',
    accessorKey: 'owner_name',
    meta: { sortField: 'owner_name' },
    size: 130,
    cell: ({ row }) => (
      <span className="truncate block max-w-[130px]">{row.original.owner_name || '-'}</span>
    ),
  },
  {
    id: 'created_date',
    header: 'Created',
    accessorKey: 'created_date',
    meta: { sortField: 'created_date' },
    size: 130,
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground">
        {formatDate ? formatDate(row.original.created_date) : row.original.created_date || '-'}
      </span>
    ),
  },
  {
    id: 'days_open',
    header: 'Days Open',
    size: 80,
    cell: ({ row }) => {
      const created = row.original.created_date
      if (!created) return <span className="text-muted-foreground">-</span>
      const days = Math.floor((Date.now() - new Date(created).getTime()) / 86400000)
      const isClosed = row.original.status === 'Resolved' || row.original.status === 'Closed'
      return (
        <span
          className={`text-sm font-medium ${isClosed ? 'text-muted-foreground' : days > 7 ? 'text-destructive' : days > 3 ? 'text-warning-foreground' : ''}`}
        >
          {days}d
        </span>
      )
    },
  },
]

export const ticketsDefaultColumnSizing: ColumnSizingState = {
  select: 40,
  ticket_number: 70,
  subject: 280,
  status: 100,
  priority: 100,
  sla: 100,
  account_name: 160,
  contact_name: 150,
  product_name: 150,
  jira: 120,
  owner_name: 130,
  created_date: 130,
  days_open: 80,
}
